import {
  ActionIcon,
  AutocompleteProps,
  FocusTrap,
  Group,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  useDebouncedCallback,
} from '@mantine/hooks';
import {
  modals,
} from '@mantine/modals';
import AddIcon from '@mui/icons-material/Add';
import {
  useForm,
} from '@tanstack/react-form';
import {
  createFileRoute,
} from '@tanstack/react-router';
import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useAuth,
} from 'react-oidc-context';

import {
  UserDetailModal,
} from '#features/settings/UserDetailModal';
import InviteModal from '#features/settings/InviteModal';
import UploadBulk from '#features/settings/UploadBulk';
import UserTable from '#features/settings/UserTable';
import BackgroundLetterAvatar from '#src/components/ui/avatar/BackgroundLetterAvatar';
import SearchInput from '#src/components/ui/input/SearchInput';

interface UserParsed {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  value?: string;
}
export const Route = createFileRoute('/_app/_authenticated/settings/users')({
  beforeLoad: () => {
    return {
      crumb: 'Users',
    };
  },
  loader: async (opts) => {
    const usersData: any = await opts.context.queryClient.ensureQueryData({
      queryKey: ['users'],
    });
    return usersData.filter(user => user.email !== opts.context.auth.user?.profile.email);
  },
  component: RouteComponent,
});

function fullNameFromUser(
  user: UserParsed,
) {
  if (user) {
    return `${user.firstName} ${user.lastName}`;
  }
}

function RouteComponent(): React.ReactNode {
  const userData = Route.useLoaderData() as UserParsed[];
  const [
    filteredUserData,
    setFilteredUserData,
  ] = useState(userData);
  const entryValues = useMemo(() => {
    return userData.map(entry => [...Object.values(entry ?? {}).filter(item => typeof item === 'string')].filter(entry => entry ? entry : false),
    ) as string[][];
  }, [userData]);
  const autoCompleteValues = useMemo(() => {
    return userData.map(entry => fullNameFromUser(entry)) as string[];
  }, [userData]);
  const handleSearch = useDebouncedCallback(async (formData) => {
    console.debug('Handle search form data', formData);
    const searchTerm = formData.searchTerm;
    const filterResult = userData.filter((_entry: UserParsed, entryIndex: keyof (typeof entryValues)) => {
      const entryProperties = entryValues[entryIndex] as string[];
      console.debug(entryProperties);
      return entryProperties.some((val) => {
        return (
          typeof val === 'string'
          && val
            .toLowerCase()
            .includes(
              searchTerm.toLowerCase(),
            )
        );
      });
    });
    console.debug(
      `Search term ${searchTerm}, matches ${filterResult.length} / ${userData.length}`,
      userData,
    );
    setFilteredUserData(filterResult);
  }, 250);
  const {
    Field,
    handleSubmit,
    state,
  } = useForm({
    defaultValues: {
      searchTerm: '',
    },
    onSubmit: async ({
      value,
    }) => {
      handleSearch(value);
    },
  });
  const renderSearchAutocompleteOption: AutocompleteProps['renderOption'] = ({
    option,
  }) => {
    const user: UserParsed | undefined = userData.find(user => fullNameFromUser(user) === option.value);
    const username: string | undefined = fullNameFromUser(user);
    return (
      <Group
        gap="sm"
        preventGrowOverflow={ false }
        grow
      >
        <BackgroundLetterAvatar
          userInitials={ `${user?.firstName[0]}${user?.lastName[0]}` }
          username={ username }
        />
        <Stack
          align="stretch"
          justify="flex-start"
          gap="xs"
        >
          <Text
            size="sm"
          >
            { username }
          </Text>
          <Text
            size="xs"
            opacity={ 0.5 }
          >
            { user?.email }
          </Text>
        </Stack>
      </Group>
    );
  };

  return (
    <>
      <div
        style={ {
          margin: '0.5vw 0',
          marginBottom: '1vw',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        } }
      >
        <div
          style={ {
            backgroundColor: 'rgb(255, 255, 255, 0.08)',
            borderRadius: '50px',
            padding: '0.2vw',
          } }
        >

          <form
            id="user-search-bar"
            onSubmit={ (e) => {
              e.preventDefault();
            } }
            onChange={ (e) => {
              e.preventDefault();
              handleSubmit();
            } }
          >
            <Field
              name="searchTerm"
              children={ ({
                state,
                handleChange,
                handleBlur,
              }) => (
                <SearchInput
                  onChange={ (evt: string): void => handleChange(evt) }
                  onBlur={ handleBlur }
                  value={ state.value }
                  data={ autoCompleteValues }
                  renderOption={ renderSearchAutocompleteOption }
                />
              ) }
            />
          </form>
        </div>
        <div
          style={ {
            flex: 1,
            textAlign: 'right',
            paddingLeft: '0.5vw',
          } }
        >
          <Tooltip
            c="transparent-dark.6"
            label="Invite New User"
          >
            <ActionIcon
              id="invite-new-user"
              variant="gradient"
              size="xl"
              radius="xl"
              aria-label="Invite new user"
              gradient={ {
                from: 'transparent.1',
                to: 'transparent.2',
                deg: 30,
              } }
              onClick={ () => {
                const inviteModalId = modals.open({
                  closeOnClickOutside: true,
                  withCloseButton: true,
                  withinPortal: true,
                  title: (
                    <Text
                      c="light.2"
                      size="xl"
                    >
                      Invite New User
                    </Text>
                  ),
                  children: (
                    <>
                      <FocusTrap.InitialFocus />
                      <InviteModal
                        show={ true }
                        onClose={ () => modals.close(inviteModalId) }
                      />
                    </>
                  ),
                });
              } }
            >
              <AddIcon />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>
      <UserTable
        userData={ filteredUserData }
        openModalHandler={ (userId: string) => {
          const userDetailModalId = modals.open({
            closeOnClickOutside: true,
            withCloseButton: true,
            withinPortal: true,
            title: (
              <Text
                c="light.2"
                size="xl"
              >
                Invite New User
              </Text>
            ),
            children: (
              <>
                <FocusTrap.InitialFocus />
                <UserDetailModal
                  show={ true }
                  onClose={ () => modals.close(userDetailModalId) }
                  userId={ userId }
                />
              </>
            ),
          });
        } }
        isLoading={ false }
      />
    </>
  );
}
